import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock } from "lucide-react";

// Global "Follow-Ups Due" bell - a separate icon/badge from NotificationBell
// (patient messages) so the two notification types are never confused at a
// glance, even though both are polled together in DashboardLayout.js and
// rendered side by side there. Clicking an item jumps straight to booking
// that patient's next appointment (AppointmentNew.js's existing phone-prefill
// hand-off), since that's the one action a doctor follow-up reminder is
// actually asking for - see ConsultationFollowUpModel/FollowUpTable.js.
export default function FollowUpBell({ followUps = { due_count: 0, items: [] }, iconClassName = "" }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const goToBookFollowUp = (item) => {
    setIsOpen(false);
    navigate("/appointment-new", { state: { newPatientPhone: item.patient_phone } });
  };

  const dueCount = followUps.due_count || 0;

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        className={`relative p-2 rounded-xl text-secondary-500 hover:bg-amber-50 hover:text-amber-600 transition-all ${iconClassName}`}
        title="Follow-Ups Due"
      >
        <CalendarClock size={18} />
        {dueCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
            {dueCount > 9 ? "9+" : dueCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="px-3 py-2 border-b border-secondary-100 flex items-center justify-between">
            <span className="text-xs font-bold text-secondary-700">Follow-Ups Due</span>
            {dueCount > 0 && (
              <span className="text-[10px] font-semibold text-amber-700">{dueCount} due</span>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {followUps.items.length === 0 ? (
              <p className="px-3 py-4 text-xs text-secondary-400 text-center">No follow-ups due</p>
            ) : (
              followUps.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => goToBookFollowUp(item)}
                  className="w-full text-left px-3 py-2.5 hover:bg-amber-50 transition-colors border-b border-secondary-50 last:border-0"
                  title="Book this patient's next appointment"
                >
                  <div className="flex items-start gap-2">
                    <CalendarClock size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-secondary-800 truncate">{item.patient_name || "Patient"}</p>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">
                          {item.days_overdue > 0 ? `${item.days_overdue}d overdue` : "Due today"}
                        </span>
                      </div>
                      <p className="text-[11px] text-secondary-400 truncate">{item.body || "Follow-up reminder"}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
